import { create } from 'zustand'

export type ApiSource = 'bcb' | 'boc'

export const CONSECUTIVE_FAILURE_THRESHOLD = 3

export interface ApiHealthState {
  failures: Record<ApiSource, number>
  recordSuccess: (source: ApiSource) => void
  recordFailure: (source: ApiSource) => void
  reset: () => void
}

/**
 * Tracks consecutive failures per upstream API. Reset to 0 on every
 * success, incremented on every failure. The UI surfaces a banner once
 * any source crosses CONSECUTIVE_FAILURE_THRESHOLD.
 *
 * State lives only in memory — failures shouldn't survive a reload.
 */
export const useApiHealthStore = create<ApiHealthState>()((set) => ({
  failures: { bcb: 0, boc: 0 },
  recordSuccess: (source) =>
    set((s) =>
      s.failures[source] === 0
        ? s
        : { failures: { ...s.failures, [source]: 0 } },
    ),
  recordFailure: (source) =>
    set((s) => ({
      failures: { ...s.failures, [source]: s.failures[source] + 1 },
    })),
  reset: () => set({ failures: { bcb: 0, boc: 0 } }),
}))

export function downSources(failures: ApiHealthState['failures']): ApiSource[] {
  const down: ApiSource[] = []
  for (const source of ['bcb', 'boc'] as const) {
    if (failures[source] >= CONSECUTIVE_FAILURE_THRESHOLD) down.push(source)
  }
  return down
}
